import { supabase } from '../lib/supabase';
import {
  AgeBand,
  ClinicalRecord,
  ConsultationPhoto,
  ControlPlace,
  InjectionPoint,
  GlucoseTargets,
  HistoryRecord,
  InsulinCategory,
  InsulinProduct,
  InsulinScheduleEntry,
  InsulinScheduleView,
  InsulinSlot,
  AgeGroup,
  MonthlyReport,
  MonthlyReportSummary,
  ProfileDetails,
  ReminderPreferences,
  SocioEconomicData
} from '../types/domain';
import {
  DEFAULT_GLUCOSE_MESSAGES,
  DEFAULT_GLUCOSE_THRESHOLDS,
  avatarOptions,
  familyIncomeOptions,
  financingTypeOptions,
  genderOptions,
  isValidMedicalRecordNo,
  isValidParentContact,
  normalizeMedicalRecordNo,
  normalizeParentContact,
  parentEducationOptions
} from '../lib/domain-data';
import { mapReportRow, monthStart } from '../lib/monthlyReport';
import { CONSENT_VERSION } from '../lib/consent';
import type { PushSubscriptionKeys } from '../lib/push';
import { Database } from '../types/supabase';

type InjectionHistoryInsert = Database['public']['Tables']['injection_history']['Insert'];

const LAST_SITE_KEY_KIDS = 'lastSite_2_10';
const LAST_SITE_KEY_TEEN = 'lastSite_11_plus';
const HISTORY_KEY_KIDS = 'history_2_10';
const HISTORY_KEY_TEEN = 'history_11_plus';

const CACHE_HISTORY_PREFIX = 'sb_cache_history_';
/** PostgREST's default row cap; getScoringLog pages at it (FR-19). */
const SCORING_PAGE_SIZE = 1000;
const CACHE_LAST_SITE_PREFIX = 'sb_cache_last_site_';
const CACHE_TARGETS_PREFIX = 'sb_cache_targets_';
const CACHE_CLINICAL_PREFIX = 'sb_cache_clinical_';
const CACHE_PRODUCTS_KEY = 'sb_cache_insulin_products';
const CACHE_SCHEDULE_PREFIX = 'sb_cache_insulin_schedule_';
const CACHE_REPORT_PREFIX = 'sb_cache_report_';
const OFFLINE_QUEUE_PREFIX = 'sb_offline_queue_';

interface OfflineAction {
  type: 'add_injection' | 'update_last_site';
  payload: any;
  timestamp: number;
}

/** Maps a snake_case insulin_products row (FR-04). */
export function mapProductRow(row: any): InsulinProduct {
  return {
    id: row.id,
    name: row.name,
    genericName: row.generic_name ?? null,
    category: row.category as InsulinCategory,
    sortOrder: row.sort_order ?? 0
  };
}

/** Maps a snake_case insulin_schedules row (FR-04). */
export function mapScheduleRow(row: any): InsulinScheduleEntry {
  return {
    id: row.id,
    userId: row.user_id,
    slot: row.slot as InsulinSlot,
    slotLabel: row.slot_label,
    timeOfDay: row.time_of_day ?? null,
    productId: row.product_id ?? null,
    customProductName: row.custom_product_name ?? null,
    dose: Number(row.dose),
    sortOrder: row.sort_order ?? 0,
    active: row.active !== false
  };
}

/**
 * Resolves a schedule row's product for display.
 *
 * A row references a catalog product or carries a custom "Lainnya" name; the
 * CHECK constraint guarantees exactly one, so the fallback here is only for a
 * row read from a stale cache.
 */
export function toScheduleView(row: any, product?: any): InsulinScheduleView {
  const entry = mapScheduleRow(row);
  return {
    ...entry,
    productLabel:
      product?.name ?? entry.customProductName ?? 'Insulin tidak tercatat',
    category: (product?.category as InsulinCategory) ?? null
  };
}

/**
 * Total Daily Dose as prescribed — the sum of the active schedule slots
 * (SoW 3.3). Distinct from what was actually injected today; see
 * getTodayInsulinTotal().
 */
export function plannedTotalDailyDose(schedule: InsulinScheduleEntry[]): number {
  return schedule
    .filter(entry => entry.active)
    .reduce((total, entry) => total + (Number(entry.dose) || 0), 0);
}

/** Maps a snake_case clinical_records row (FR-12). */
export function mapClinicalRow(row: any): ClinicalRecord {
  const num = (v: unknown) => (v === null || v === undefined ? null : Number(v));
  return {
    id: row.id,
    userId: row.user_id,
    recordedAt: row.recorded_at,
    weightKg: num(row.weight_kg),
    heightCm: num(row.height_cm),
    hba1c: num(row.hba1c)
  };
}

/**
 * BMI from a record, or null when either measure is missing.
 *
 * Derived rather than stored: storing it would let it drift from the weight
 * and height it came from. Not in the SoW, but free once both are captured
 * and the obvious thing a clinician looks for next.
 */
export function bodyMassIndex(record: ClinicalRecord): number | null {
  if (!record.weightKg || !record.heightCm) {
    return null;
  }
  const metres = record.heightCm / 100;
  return Math.round((record.weightKg / (metres * metres)) * 10) / 10;
}

/** Maps a snake_case glucose_targets row to the domain shape (FR-06). */
export function mapTargetsRow(row: any): GlucoseTargets {
  return {
    id: row.id,
    scope: row.scope === 'patient' ? 'patient' : 'global',
    patientId: row.patient_id ?? null,
    pre: {
      low: row.pre_low,
      normalMax: row.pre_normal_max,
      warningMax: row.pre_warning_max
    },
    post: {
      low: row.post_low,
      normalMax: row.post_normal_max,
      warningMax: row.post_warning_max
    },
    messages: {
      low: row.msg_low,
      normal: row.msg_normal,
      warning: row.msg_warning,
      high: row.msg_high
    },
    updatedAt: row.updated_at ?? null,
    updatedBy: row.updated_by ?? null
  };
}

/**
 * The seeded values, as a GlucoseTargets.
 *
 * Used when the table cannot be reached at all — a cold offline start, or a
 * deploy that ran before the migration. It matches what the migration seeds,
 * so this fallback and a freshly migrated database behave identically.
 */
export const FALLBACK_TARGETS: GlucoseTargets = {
  id: 'fallback',
  scope: 'global',
  patientId: null,
  pre: { ...DEFAULT_GLUCOSE_THRESHOLDS },
  post: { ...DEFAULT_GLUCOSE_THRESHOLDS },
  messages: { ...DEFAULT_GLUCOSE_MESSAGES },
  updatedAt: null,
  updatedBy: null
};

/**
 * Maps a snake_case injection_history row to a HistoryRecord.
 *
 * Shared by the live and the cached read paths in getHistory — they used to
 * carry two verbatim copies of this, which is how a new column gets wired into
 * one path and silently dropped from the other.
 */
function mapHistoryRow(row: any): HistoryRecord {
  return {
    date: row.date,
    time: row.time,
    siteId: row.site_id,
    insulinTypeId: row.insulin_type_id,
    ageGroup: row.age_group as AgeGroup,
    insulinBrand: row.insulin_brand,
    insulinProductId: row.insulin_product_id ?? null,
    insulinDose: row.insulin_dose,
    glucoseValue: row.glucose_value,
    measurementContext: row.measurement_context ?? null,
    regionId: row.region_id ?? null,
    pointRow: row.point_row ?? null,
    pointCol: row.point_col ?? null,
    lipodystrophy: row.lipodystrophy === true
  };
}

/**
 * Mirrors the CHECK constraints on insulin_schedules so a bad entry is refused
 * with a readable Indonesian message rather than a Postgres error.
 */
function assertScheduleEntry(entry: {
  slotLabel: string;
  productId: string | null;
  customProductName: string | null;
  dose: number;
}): void {
  if (!entry.slotLabel.trim()) {
    throw new Error('Nama jadwal tidak boleh kosong.');
  }
  if (!entry.productId && !entry.customProductName?.trim()) {
    throw new Error('Pilih jenis insulin, atau tulis sendiri jika memilih Lainnya.');
  }
  if (entry.productId && entry.customProductName?.trim()) {
    throw new Error('Pilih dari daftar atau tulis sendiri, tidak keduanya.');
  }
  if (!Number.isFinite(entry.dose) || entry.dose <= 0) {
    throw new Error('Dosis insulin harus lebih dari 0 unit.');
  }
  if (entry.dose > 200) {
    throw new Error('Dosis insulin maksimal 200 unit.');
  }
}

/**
 * Mirrors the CHECK constraints on clinical_records so a bad entry gets a
 * readable Indonesian message rather than a Postgres error. The bounds are
 * deliberately wide — they catch a misplaced decimal or a gram/kilogram
 * mix-up, not a clinically unusual child.
 */
function assertClinicalRecord(record: {
  recordedAt: string;
  weightKg: number | null;
  heightCm: number | null;
  hba1c: number | null;
}): void {
  if (!record.recordedAt) {
    throw new Error('Tanggal pemeriksaan harus diisi.');
  }
  if (record.weightKg === null && record.heightCm === null && record.hba1c === null) {
    throw new Error('Isi minimal satu: berat badan, tinggi badan, atau HbA1c.');
  }
  if (record.weightKg !== null && !(record.weightKg > 0 && record.weightKg <= 250)) {
    throw new Error('Berat badan harus antara 0 dan 250 kg.');
  }
  if (record.heightCm !== null && !(record.heightCm >= 30 && record.heightCm <= 250)) {
    throw new Error('Tinggi badan harus antara 30 dan 250 cm.');
  }
  if (record.hba1c !== null && !(record.hba1c >= 2 && record.hba1c <= 20)) {
    throw new Error('HbA1c harus antara 2 dan 20 %.');
  }
}

/**
 * Mirrors the FR-03 CHECK constraints on `profiles` (migration 012).
 *
 * The coded fields are validated against the same lists the pickers are built
 * from, so a stale cached bundle cannot post a code the database will reject
 * with an unreadable constraint error. The contact number is checked strictly
 * here and loosely in SQL — the friendly half of that split.
 */
function assertSocioEconomic(data: SocioEconomicData): void {
  const codes: [string | null, { id: string }[], string][] = [
    [data.parentEducation, parentEducationOptions, 'Pendidikan orang tua'],
    [data.familyIncome, familyIncomeOptions, 'Penghasilan keluarga'],
    [data.financingType, financingTypeOptions, 'Jenis pembiayaan']
  ];

  for (const [value, options, label] of codes) {
    if (value !== null && !options.some(option => option.id === value)) {
      throw new Error(`${label} tidak dikenali.`);
    }
  }

  if (data.parentContact !== null && !isValidParentContact(data.parentContact)) {
    throw new Error(
      'Nomor kontak orang tua harus berupa nomor Indonesia, contoh 081234567890.'
    );
  }
}

/**
 * Normalises then validates an FR-03 answer set, returning the canonical form.
 *
 * Both write paths go through this: `updateSocioEconomic` for an edit, and
 * `/complete-profile`, which folds the fields into the one upsert that creates
 * the profile rather than saving them in a second round trip. Exported so the
 * onboarding form cannot quietly skip the normalisation step.
 */
export function prepareSocioEconomic(data: SocioEconomicData): SocioEconomicData {
  const normalized: SocioEconomicData = {
    ...data,
    parentContact: normalizeParentContact(data.parentContact)
  };
  assertSocioEconomic(normalized);
  return normalized;
}

/** The prepared answers as the four `profiles` columns migration 012 adds. */
export function socioEconomicColumns(data: SocioEconomicData): {
  parent_education: string | null;
  family_income: string | null;
  financing_type: string | null;
  parent_contact: string | null;
} {
  return {
    parent_education: data.parentEducation,
    family_income: data.familyIncome,
    financing_type: data.financingType,
    parent_contact: data.parentContact
  };
}

/** A doctor the patient can pick (FR-02): an account assigned to a hospital. */
export interface DoctorOption {
  id: string;
  name: string;
}

/**
 * The treating doctor as it will be stored. `id` is null when the doctor has
 * no account yet and the family typed the name instead.
 */
export interface ResolvedDoctor {
  id: string | null;
  name: string;
}

function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(new Date(value).getTime());
}

function localToday(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

/**
 * Normalises then validates the FR-02 fields, returning the canonical form.
 *
 * Everything except the medical record number is required (SoW 2.2). The
 * database only guards shape (migration 013) — the columns are nullable so
 * pre-FR-02 profiles stay valid — so this is where "required" is enforced,
 * for every form that writes a profile.
 */
export function prepareProfileDetails(details: ProfileDetails): ProfileDetails {
  const normalized: ProfileDetails = {
    ...details,
    fullName: details.fullName.trim().replace(/\s+/g, ' '),
    diabetesType: details.diabetesType.trim(),
    medicalRecordNo: normalizeMedicalRecordNo(details.medicalRecordNo)
  };
  const today = localToday();

  if (!normalized.fullName) {
    throw new Error('Nama lengkap wajib diisi.');
  }
  if (!isIsoDate(normalized.birthDate)) {
    throw new Error('Tanggal lahir wajib diisi.');
  }
  if (normalized.birthDate > today) {
    throw new Error('Tanggal lahir tidak boleh di masa depan.');
  }
  if (!normalized.gender || !genderOptions.some(o => o.id === normalized.gender)) {
    throw new Error('Jenis kelamin wajib dipilih.');
  }
  if (!normalized.avatarId || !avatarOptions.some(o => o.id === normalized.avatarId)) {
    throw new Error('Pilih salah satu karakter avatar.');
  }
  if (!normalized.diabetesType) {
    throw new Error('Tipe diabetes wajib dipilih.');
  }
  if (!isIsoDate(normalized.diagnosisDate)) {
    throw new Error('Tanggal diagnosis wajib diisi.');
  }
  if (normalized.diagnosisDate > today) {
    throw new Error('Tanggal diagnosis tidak boleh di masa depan.');
  }
  if (normalized.diagnosisDate < normalized.birthDate) {
    throw new Error('Tanggal diagnosis tidak boleh sebelum tanggal lahir.');
  }
  if (
    normalized.medicalRecordNo !== null &&
    !isValidMedicalRecordNo(normalized.medicalRecordNo)
  ) {
    throw new Error(
      'Nomor rekam medis maksimal 30 karakter: huruf, angka, titik, garis miring, atau tanda hubung.'
    );
  }
  return normalized;
}

/**
 * The prepared FR-02 fields plus the resolved hospital and doctor as
 * `profiles` columns. One function, so onboarding, registration metadata and
 * the profile editor cannot store different shapes.
 */
export function profileDetailsColumns(
  details: ProfileDetails,
  place: { id: string; name: string },
  doctor: ResolvedDoctor
): {
  full_name: string;
  birth_date: string;
  gender: string | null;
  avatar_id: string | null;
  diabetes_type: string;
  medical_record_no: string | null;
  diagnosis_date: string;
  hospital_id: string;
  control_place: string;
  doctor_id: string | null;
  doctor_name: string;
} {
  const doctorName = doctor.name.trim().replace(/\s+/g, ' ');
  if (!doctorName) {
    throw new Error('Nama dokter wajib dipilih.');
  }
  return {
    full_name: details.fullName,
    birth_date: details.birthDate,
    gender: details.gender,
    avatar_id: details.avatarId,
    diabetes_type: details.diabetesType,
    medical_record_no: details.medicalRecordNo,
    diagnosis_date: details.diagnosisDate,
    hospital_id: place.id,
    control_place: place.name,
    doctor_id: doctor.id,
    doctor_name: doctorName
  };
}

/** FR-17 storage bucket (migration 014). Private; read only via signed URLs. */
export const CONSULTATION_BUCKET = 'consultation-photos';
/** Signed URLs live an hour: long enough to look through a visit's photos,
 *  short enough that a URL copied out of the page soon stops working. */
const SIGNED_URL_SECONDS = 60 * 60;
/** Matches the bucket's file_size_limit and the table's CHECK. */
export const CONSULTATION_MAX_BYTES = 5 * 1024 * 1024;

function mapPhotoRow(row: any, url: string | null): ConsultationPhoto {
  return {
    id: row.id,
    userId: row.user_id,
    consultationDate: row.consultation_date,
    storagePath: row.storage_path,
    sizeBytes: row.size_bytes,
    createdAt: row.created_at,
    url
  };
}

/** Migration 016's column defaults — what a patient with no row gets. */
export const DEFAULT_REMINDER_PREFERENCES: ReminderPreferences = {
  insulin: true,
  glucose: true,
  hba1c: true,
  controlVisit: true,
  dailyLog: true,
  glucoseTime: '07:00',
  dailyLogTime: '20:00',
  timezone: 'Asia/Jakarta'
};

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export const dataService = {
  async migrateLocalData(userId: string) {
    const kidsHistoryRaw = localStorage.getItem(HISTORY_KEY_KIDS);
    const teenHistoryRaw = localStorage.getItem(HISTORY_KEY_TEEN);
    const kidsLastSite = localStorage.getItem(LAST_SITE_KEY_KIDS);
    const teenLastSite = localStorage.getItem(LAST_SITE_KEY_TEEN);

    if (!kidsHistoryRaw && !teenHistoryRaw && !kidsLastSite && !teenLastSite) {
      return;
    }

    const recordsToInsert: InjectionHistoryInsert[] = [];

    if (kidsHistoryRaw) {
      try {
        const parsed = JSON.parse(kidsHistoryRaw);
        if (Array.isArray(parsed)) {
          parsed.forEach((entry: any) => {
             recordsToInsert.push({
               user_id: userId,
               date: entry.date,
               time: entry.time,
               site_id: entry.siteId,
               insulin_type_id: entry.insulinTypeId,
               age_group: '2-10'
             });
          });
        }
      } catch (e) { console.error(e); }
    }

    if (teenHistoryRaw) {
      try {
        const parsed = JSON.parse(teenHistoryRaw);
        if (Array.isArray(parsed)) {
          parsed.forEach((entry: any) => {
             recordsToInsert.push({
               user_id: userId,
               date: entry.date,
               time: entry.time,
               site_id: entry.siteId,
               insulin_type_id: entry.insulinTypeId,
               age_group: '11-plus'
             });
          });
        }
      } catch (e) { console.error(e); }
    }

    if (recordsToInsert.length > 0) {
      const { error } = await supabase.from('injection_history').insert(recordsToInsert);
      if (error) console.error('Migration history error:', error);
    }

    const { data: existingLastSiteData } = await supabase
        .from('last_injection_site')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

    const existingLastSite = existingLastSiteData as any;

    const lastSiteData: any = {
        user_id: userId,
        updated_at: new Date().toISOString(),
        last_site_id_kids: existingLastSite?.last_site_id_kids ?? (kidsLastSite ? parseInt(kidsLastSite) : null),
        last_site_id_teen: existingLastSite?.last_site_id_teen ?? (teenLastSite ? parseInt(teenLastSite) : null)
    };

    const { error: lastSiteError } = await supabase.from('last_injection_site').upsert(lastSiteData);

    if (lastSiteError) console.error('Migration last site error:', lastSiteError);

    localStorage.removeItem(HISTORY_KEY_KIDS);
    localStorage.removeItem(HISTORY_KEY_TEEN);
    localStorage.removeItem(LAST_SITE_KEY_KIDS);
    localStorage.removeItem(LAST_SITE_KEY_TEEN);
  },

  /**
   * The hospitals a patient can pick from (DF-09).
   *
   * Reads the normalized control_places table, not the distinct free text that
   * patients previously typed. Pending entries are included so a patient who
   * created one via "Lainnya" still sees their own choice while it awaits
   * approval — ControlPlaceSelect hides every other pending entry.
   */
  async getControlPlaces(): Promise<ControlPlace[]> {
    const { data, error } = await supabase
      .from('control_places')
      .select('id, name, status')
      .order('status', { ascending: true })
      .order('name', { ascending: true });

    if (error) throw error;
    return (data ?? []) as ControlPlace[];
  },

  /**
   * Resolves a hospital typed into "Lainnya" to a control_places row.
   *
   * The unique index on the generated name_normalized column does the work: a
   * name that differs from an existing one only by case or whitespace resolves
   * to that existing row rather than creating a near-duplicate. Genuinely new
   * names land as 'pending' for an admin to approve or merge, so free text can
   * never silently become a hospital of record.
   */
  /**
   * Resolves a hospital typed into "Lainnya" to a control_places row.
   *
   * A name differing from an existing one only by case or whitespace resolves
   * to that existing row rather than creating a near-duplicate — the unique
   * index on the generated name_normalized column guarantees it. Genuinely new
   * names land as 'pending' for an admin to approve or merge, so free text can
   * never silently become a hospital of record. That is what stops "Lainnya"
   * from simply recreating DF-09.
   */
  async findOrCreateControlPlace(rawName: string): Promise<ControlPlace> {
    const name = rawName.trim().replace(/\s+/g, ' ');
    if (!name) {
      throw new Error('Nama tempat kontrol tidak boleh kosong.');
    }
    // Mirrors the SQL generated column: btrim + collapse whitespace + lower.
    const normalized = name.toLowerCase();

    const readExisting = async () => {
      const { data } = await supabase
        .from('control_places')
        .select('id, name, status')
        .eq('name_normalized', normalized)
        .maybeSingle();
      return (data as ControlPlace | null) ?? null;
    };

    // Read first. Deliberately NOT an upsert: an upsert would carry
    // status:'pending' into the ON CONFLICT UPDATE and demote an existing
    // approved hospital back to pending.
    const existing = await readExisting();
    if (existing) {
      return existing;
    }

    const { data, error } = await supabase
      .from('control_places')
      .insert({ name, status: 'pending' })
      .select('id, name, status')
      .single();

    if (data) {
      return data as ControlPlace;
    }

    // Lost a race against another client inserting the same name.
    const raced = await readExisting();
    if (raced) {
      return raced;
    }
    throw error ?? new Error('Gagal menyimpan tempat kontrol.');
  },

  /**
   * The target ranges a given patient's readings are judged against (FR-06).
   *
   * Resolution is override-then-global: a per-patient row wins, otherwise the
   * single global row. Cached per user because CheckGlucose needs it to colour
   * a reading and must keep working offline; on any failure the last known
   * targets stand, and failing that the seeded defaults, so a reading is never
   * left uncoloured.
   */
  async getEffectiveTargets(userId: string): Promise<GlucoseTargets> {
    try {
      // Two parameterised queries rather than one .or() with the id
      // interpolated into a PostgREST filter string — same wall-clock cost,
      // and no filter-injection surface if the id is ever not a plain uuid.
      const [overrideResult, globalResult] = await Promise.all([
        supabase.from('glucose_targets').select('*').eq('patient_id', userId).maybeSingle(),
        supabase.from('glucose_targets').select('*').eq('scope', 'global').maybeSingle()
      ]);

      if (globalResult.error) throw globalResult.error;

      const chosen = overrideResult.data ?? globalResult.data;
      if (!chosen) throw new Error('No glucose_targets row');

      const targets = mapTargetsRow(chosen);
      localStorage.setItem(CACHE_TARGETS_PREFIX + userId, JSON.stringify(targets));
      return targets;
    } catch (err) {
      console.warn('Offline mode or error fetching glucose targets, using cache:', err);
      const cached = localStorage.getItem(CACHE_TARGETS_PREFIX + userId);
      if (cached) {
        try {
          return JSON.parse(cached) as GlucoseTargets;
        } catch {
          return FALLBACK_TARGETS;
        }
      }
      return FALLBACK_TARGETS;
    }
  },

  /**
   * The curated insulin catalog (FR-04, SoW 3.2).
   *
   * Cached because it is effectively static and the schedule editor needs it
   * to render at all — a patient offline should still be able to read their
   * own regimen.
   */
  async getInsulinProducts(): Promise<InsulinProduct[]> {
    try {
      const { data, error } = await supabase
        .from('insulin_products')
        .select('*')
        .eq('active', true)
        .order('category', { ascending: true })
        .order('sort_order', { ascending: true });

      if (error) throw error;

      const products = (data ?? []).map(mapProductRow);
      localStorage.setItem(CACHE_PRODUCTS_KEY, JSON.stringify(products));
      return products;
    } catch (err) {
      console.warn('Offline mode or error fetching insulin catalog, using cache:', err);
      const cached = localStorage.getItem(CACHE_PRODUCTS_KEY);
      if (cached) {
        try {
          return JSON.parse(cached) as InsulinProduct[];
        } catch {
          return [];
        }
      }
      return [];
    }
  },

  /** A patient's active regimen slots, product names resolved for display. */
  async getSchedule(userId: string): Promise<InsulinScheduleView[]> {
    try {
      const { data, error } = await supabase
        .from('insulin_schedules')
        .select('*, insulin_products(name, category)')
        .eq('user_id', userId)
        .eq('active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      const rows = (data ?? []) as any[];
      const views = rows.map(row => toScheduleView(row, row.insulin_products));
      localStorage.setItem(CACHE_SCHEDULE_PREFIX + userId, JSON.stringify(views));
      return views;
    } catch (err) {
      console.warn('Offline mode or error fetching insulin schedule, using cache:', err);
      const cached = localStorage.getItem(CACHE_SCHEDULE_PREFIX + userId);
      if (cached) {
        try {
          return JSON.parse(cached) as InsulinScheduleView[];
        } catch {
          return [];
        }
      }
      return [];
    }
  },

  /** A patient's clinical records, newest first (FR-12). */
  async getClinicalRecords(userId: string): Promise<ClinicalRecord[]> {
    try {
      const { data, error } = await supabase
        .from('clinical_records')
        .select('*')
        .eq('user_id', userId)
        .order('recorded_at', { ascending: false });

      if (error) throw error;

      const records = (data ?? []).map(mapClinicalRow);
      localStorage.setItem(CACHE_CLINICAL_PREFIX + userId, JSON.stringify(records));
      return records;
    } catch (err) {
      console.warn('Offline mode or error fetching clinical records, using cache:', err);
      const cached = localStorage.getItem(CACHE_CLINICAL_PREFIX + userId);
      if (cached) {
        try {
          return JSON.parse(cached) as ClinicalRecord[];
        } catch {
          return [];
        }
      }
      return [];
    }
  },

  /**
   * Creates or replaces the record for a date.
   *
   * Upsert on (user_id, recorded_at): re-entering a date corrects that visit
   * rather than producing two readings for the same test.
   */
  async saveClinicalRecord(
    userId: string,
    record: {
      recordedAt: string;
      weightKg: number | null;
      heightCm: number | null;
      hba1c: number | null;
    }
  ): Promise<void> {
    assertClinicalRecord(record);

    const { error } = await supabase.from('clinical_records').upsert(
      {
        user_id: userId,
        recorded_at: record.recordedAt,
        weight_kg: record.weightKg,
        height_cm: record.heightCm,
        hba1c: record.hba1c,
        updated_at: new Date().toISOString()
      },
      { onConflict: 'user_id,recorded_at' }
    );

    if (error) throw error;
    localStorage.removeItem(CACHE_CLINICAL_PREFIX + userId);
  },

  async deleteClinicalRecord(userId: string, id: string): Promise<void> {
    const { error } = await supabase
      .from('clinical_records')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) throw error;
    localStorage.removeItem(CACHE_CLINICAL_PREFIX + userId);
  },

  /**
   * An admin-managed switch (migration 014), or `fallback` when the row is
   * missing or unreadable.
   *
   * The fallback matters offline: a feature flag that cannot be read must not
   * make the app look broken, so each caller picks the safe default for its
   * feature rather than this function guessing one.
   */
  async getAppSetting<T>(key: string, fallback: T): Promise<T> {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', key)
        .maybeSingle();
      if (error || !data) {
        return fallback;
      }
      return (data as { value: T }).value;
    } catch {
      return fallback;
    }
  },

  /**
   * A patient's consultation photos, newest consultation first, each with a
   * fresh signed URL (FR-17). One createSignedUrls call for the whole list,
   * not one per photo.
   */
  async listConsultationPhotos(userId: string): Promise<ConsultationPhoto[]> {
    const { data, error } = await supabase
      .from('consultation_photos')
      .select('*')
      .eq('user_id', userId)
      .order('consultation_date', { ascending: false })
      .order('created_at', { ascending: false });
    if (error) throw error;

    const rows = (data ?? []) as any[];
    if (rows.length === 0) {
      return [];
    }

    const { data: signed, error: signError } = await supabase.storage
      .from(CONSULTATION_BUCKET)
      .createSignedUrls(
        rows.map(r => r.storage_path as string),
        SIGNED_URL_SECONDS
      );
    if (signError) throw signError;

    const urlFor = new Map(
      (signed ?? []).map((s: any) => [s.path as string, (s.signedUrl as string | null) ?? null])
    );
    return rows.map(row => mapPhotoRow(row, urlFor.get(row.storage_path) ?? null));
  },

  /**
   * Uploads one prepared JPEG and records it (FR-17).
   *
   * Not queued offline, unlike addInjection: the offline queue lives in
   * localStorage, which cannot hold a few megabytes of image, and a photo the
   * parent is looking at can simply be uploaded again. So it fails loudly.
   *
   * Object first, then row. If the row insert fails the object is removed
   * again, so a failed upload does not leave an orphan in the bucket.
   */
  async uploadConsultationPhoto(
    userId: string,
    image: Blob,
    consultationDate: string
  ): Promise<void> {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(consultationDate)) {
      throw new Error('Tanggal konsultasi wajib diisi.');
    }
    if (consultationDate > localToday()) {
      throw new Error('Tanggal konsultasi tidak boleh di masa depan.');
    }
    if (image.size <= 0 || image.size > CONSULTATION_MAX_BYTES) {
      throw new Error('Ukuran foto maksimal 5 MB.');
    }

    const path = `${userId}/${crypto.randomUUID()}.jpg`;
    const { error: uploadError } = await supabase.storage
      .from(CONSULTATION_BUCKET)
      .upload(path, image, { contentType: 'image/jpeg', upsert: false });
    if (uploadError) throw uploadError;

    const { error: insertError } = await supabase.from('consultation_photos').insert({
      user_id: userId,
      consultation_date: consultationDate,
      storage_path: path,
      content_type: 'image/jpeg',
      size_bytes: image.size
    });
    if (insertError) {
      await supabase.storage.from(CONSULTATION_BUCKET).remove([path]);
      throw insertError;
    }
  },

  /**
   * Deletes a photo the family uploaded by mistake.
   *
   * Row first, then object: if the second step fails, what is left is an
   * object nothing lists — invisible — rather than a listed photo that no
   * longer loads.
   */
  async deleteConsultationPhoto(photo: ConsultationPhoto): Promise<void> {
    const { error } = await supabase
      .from('consultation_photos')
      .delete()
      .eq('id', photo.id);
    if (error) throw error;

    const { error: removeError } = await supabase.storage
      .from(CONSULTATION_BUCKET)
      .remove([photo.storagePath]);
    if (removeError) {
      console.warn('Consultation photo row deleted but object remains:', photo.storagePath);
    }
  },

  /**
   * Records the patient's answer to the current consent text (FR-22).
   *
   * The version is stamped here, from the bundle, not passed in: an answer is
   * only ever recorded against the text this build actually showed.
   */
  async setResearchConsent(userId: string, agree: boolean): Promise<void> {
    const { error } = await supabase
      .from('profiles')
      .update({
        research_consent: agree,
        research_consent_at: new Date().toISOString(),
        research_consent_version: CONSENT_VERSION,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);
    if (error) throw error;
  },

  /** FR-20 preferences, or the defaults when the patient never saved any. */
  async getReminderPreferences(userId: string): Promise<ReminderPreferences> {
    const { data, error } = await supabase
      .from('reminder_preferences')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) throw error;
    if (!data) {
      return DEFAULT_REMINDER_PREFERENCES;
    }
    const row = data as any;
    return {
      insulin: row.insulin,
      glucose: row.glucose,
      hba1c: row.hba1c,
      controlVisit: row.control_visit,
      dailyLog: row.daily_log,
      // Postgres returns time as 'HH:MM:SS'.
      glucoseTime: String(row.glucose_time).slice(0, 5),
      dailyLogTime: String(row.daily_log_time).slice(0, 5),
      timezone: row.timezone
    };
  },

  async saveReminderPreferences(userId: string, prefs: ReminderPreferences): Promise<void> {
    if (!TIME_PATTERN.test(prefs.glucoseTime) || !TIME_PATTERN.test(prefs.dailyLogTime)) {
      throw new Error('Jam pengingat tidak valid.');
    }
    const { error } = await supabase.from('reminder_preferences').upsert(
      {
        user_id: userId,
        insulin: prefs.insulin,
        glucose: prefs.glucose,
        hba1c: prefs.hba1c,
        control_visit: prefs.controlVisit,
        daily_log: prefs.dailyLog,
        glucose_time: prefs.glucoseTime,
        daily_log_time: prefs.dailyLogTime,
        timezone: prefs.timezone,
        updated_at: new Date().toISOString()
      },
      { onConflict: 'user_id' }
    );
    if (error) throw error;
  },

  /**
   * Stores this device's subscription. Upserted on the endpoint, which is the
   * device's identity: re-enabling on the same phone replaces its row, and a
   * shared tablet moves to whoever enabled it last.
   */
  async savePushSubscription(userId: string, keys: PushSubscriptionKeys): Promise<void> {
    const { error } = await supabase.from('push_subscriptions').upsert(
      {
        user_id: userId,
        endpoint: keys.endpoint,
        p256dh: keys.p256dh,
        auth: keys.auth,
        user_agent: navigator.userAgent.slice(0, 300),
        failure_count: 0
      },
      { onConflict: 'endpoint' }
    );
    if (error) throw error;
  },

  async deletePushSubscription(endpoint: string): Promise<void> {
    const { error } = await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint);
    if (error) throw error;
  },

  /** Asks the server to push one test notification to the caller's devices. */
  async sendTestPush(): Promise<void> {
    const { error } = await supabase.functions.invoke('send-reminders', {
      body: { test: true }
    });
    if (error) {
      // FunctionsHttpError carries the function's own message in its context.
      const detail = await (error as any).context?.json?.().catch(() => null);
      throw new Error(detail?.error || 'Gagal mengirim notifikasi uji.');
    }
  },

  /** FR-20's visit date. Null clears it. Not queued offline, like other profile edits. */
  async setNextControlDate(userId: string, date: string | null): Promise<void> {
    if (date !== null && !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      throw new Error('Tanggal kontrol tidak valid.');
    }
    if (date !== null && date < localToday()) {
      throw new Error('Tanggal kontrol berikutnya tidak boleh di masa lalu.');
    }
    const { error } = await supabase
      .from('profiles')
      .update({ next_control_date: date, updated_at: new Date().toISOString() })
      .eq('id', userId);
    if (error) throw error;
  },

  /**
   * Doctors a patient at this hospital can name as their treating doctor
   * (FR-02): the doctor accounts the admin has assigned to it. The admin's
   * assignments are the doctor list — there is no second roster (C4).
   */
  async getDoctorsForPlace(placeId: string): Promise<DoctorOption[]> {
    const { data: links, error: linkError } = await supabase
      .from('doctor_control_places')
      .select('doctor_id')
      .eq('control_place_id', placeId);
    if (linkError) throw linkError;

    const ids = Array.from(new Set((links ?? []).map((l: any) => l.doctor_id as string)));
    if (ids.length === 0) {
      return [];
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', ids)
      .eq('role', 'doctor')
      .order('full_name', { ascending: true });
    if (error) throw error;

    return (data ?? []).map((row: any) => ({
      id: row.id as string,
      name: (row.full_name as string | null) ?? 'Dokter'
    }));
  },

  /**
   * Saves the FR-02 profile fields onto the patient's own profile.
   *
   * Not queued offline, for the same reason as updateSocioEconomic below: a
   * profile edit is rare, deliberate and on screen, so failing loudly beats
   * a silently replayed edit.
   */
  async updateProfileDetails(
    userId: string,
    details: ProfileDetails,
    place: { id: string; name: string },
    doctor: ResolvedDoctor
  ): Promise<void> {
    const { error } = await supabase
      .from('profiles')
      .update({
        ...profileDetailsColumns(prepareProfileDetails(details), place, doctor),
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (error) throw error;
  },

  /**
   * Saves the FR-03 socio-economic fields onto the patient's own profile.
   *
   * Deliberately does NOT go through the offline queue, unlike addInjection.
   * The queue exists so a reading taken in a clinic basement is not lost —
   * it replays a payload the patient can no longer see. A profile edit is
   * rare, deliberate, and fully visible on screen, so failing loudly and
   * letting the parent press Simpan again is the honest behaviour; a silently
   * queued edit would leave two versions of the truth for however long the
   * device stayed offline.
   *
   * Normalisation happens here rather than in the page so every caller —
   * onboarding, the family-data screen, the doctor dashboard — stores the
   * same shape.
   */
  async updateSocioEconomic(userId: string, data: SocioEconomicData): Promise<void> {
    const { error } = await supabase
      .from('profiles')
      .update({
        ...socioEconomicColumns(prepareSocioEconomic(data)),
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (error) throw error;
  },

  /**
   * A stored monthly report snapshot (FR-16).
   *
   * Read-only: the figures are built by build_monthly_report() on the server,
   * because the report has to exist whether or not the patient ever opens the
   * app. Returns null when no snapshot exists for that month — the caller
   * decides whether to offer generateMonthlyReport().
   */
  async getMonthlyReport(userId: string, month: string): Promise<MonthlyReport | null> {
    const period = monthStart(month);
    try {
      const { data, error } = await supabase
        .from('monthly_reports')
        .select('*')
        .eq('user_id', userId)
        .eq('period_month', period)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        localStorage.setItem(
          CACHE_REPORT_PREFIX + userId + '_' + period,
          JSON.stringify(data)
        );
      }
      return data ? mapReportRow(data) : null;
    } catch (err) {
      console.warn('Offline mode or error fetching monthly report, using cache:', err);
      const cached = localStorage.getItem(CACHE_REPORT_PREFIX + userId + '_' + period);
      if (cached) {
        try {
          return mapReportRow(JSON.parse(cached));
        } catch {
          return null;
        }
      }
      return null;
    }
  },

  /** Which months already have a snapshot, newest first. */
  async listMonthlyReports(userId: string): Promise<MonthlyReportSummary[]> {
    const { data, error } = await supabase
      .from('monthly_reports')
      .select('period_month, generated_at')
      .eq('user_id', userId)
      .order('period_month', { ascending: false });

    if (error) throw error;
    return (data ?? []).map((row: any) => ({
      periodMonth: row.period_month,
      generatedAt: row.generated_at
    }));
  },

  /**
   * Asks the server to build (or rebuild) one month.
   *
   * Goes through the monthly-report Edge Function rather than calling the RPC
   * directly: with RLS excluded (URS A1) the function is the only place a
   * caller can be checked, and it runs the same SQL the cron job does, so an
   * on-demand report and a scheduled one cannot disagree.
   *
   * There is no offline queue for this. The report is derived data — nothing
   * is lost by failing, and queueing it would write a snapshot from whatever
   * the server held whenever the device next reconnected.
   */
  async generateMonthlyReport(
    userId: string,
    month: string
  ): Promise<MonthlyReport | null> {
    const period = monthStart(month);
    const { data, error } = await supabase.functions.invoke('monthly-report', {
      body: { userId, month: period }
    });

    if (error) {
      let message = 'Gagal menyusun laporan bulanan.';
      const context = (error as any).context;
      if (context && typeof context.json === 'function') {
        try {
          const body = await context.json();
          if (body?.error) {
            message = body.error;
          }
        } catch {
          // Fall through to the generic message.
        }
      }
      throw new Error(message);
    }

    if ((data as any)?.error) {
      throw new Error((data as any).error);
    }

    return data ? mapReportRow(data) : null;
  },

  /**
   * A patient's log, newest first.
   *
   * `limit` bounds the read for screens that only need the recent end of it —
   * Home wants today plus the last activity, not three years of rows (NFR-02).
   * A bounded read deliberately does NOT write the cache: the cache is what
   * HistoryList reads offline, and overwriting it with the first 200 rows
   * would silently truncate the history the patient can see with no network.
   */
  async getHistory(userId: string, limit?: number): Promise<HistoryRecord[]> {
    try {
      let query = supabase
        .from('injection_history')
        .select('*')
        .eq('user_id', userId)
        .order('date', { ascending: false })
        .order('time', { ascending: false });

      if (limit) {
        query = query.limit(limit);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (!limit) {
        // Update cache
        localStorage.setItem(CACHE_HISTORY_PREFIX + userId, JSON.stringify(data));
      }

      return data.map(mapHistoryRow);
    } catch (err) {
      console.warn('Offline mode or error fetching history, using cache:', err);
      const cached = localStorage.getItem(CACHE_HISTORY_PREFIX + userId);
      if (cached) {
        try {
          const data = JSON.parse(cached);
          return data.map(mapHistoryRow);
        } catch {
          return [];
        }
      }
      return [];
    }
  },

  /**
   * The whole log, narrowed to the columns gamification scores (FR-19).
   *
   * Points and streaks need every row, not Home's newest 60, so this pages
   * past PostgREST's 1,000-row cap the way getExportData does. Narrow columns
   * keep a years-long log cheap (NFR-02). Offline it falls back to the history
   * cache, which already holds any queued entry — so a reading taken with no
   * network still counts. It never writes that cache: these rows are partial.
   */
  async getScoringLog(userId: string): Promise<HistoryRecord[]> {
    try {
      const rows: any[] = [];
      for (let offset = 0; ; offset += SCORING_PAGE_SIZE) {
        const { data, error } = await supabase
          .from('injection_history')
          .select(
            'date, time, site_id, insulin_type_id, age_group, insulin_dose, glucose_value, measurement_context, region_id, point_row, point_col'
          )
          .eq('user_id', userId)
          .order('date', { ascending: true })
          .order('time', { ascending: true })
          .range(offset, offset + SCORING_PAGE_SIZE - 1);
        if (error) throw error;
        const page = (data ?? []) as any[];
        rows.push(...page);
        if (page.length < SCORING_PAGE_SIZE) break;
      }
      return rows.map(mapHistoryRow);
    } catch (err) {
      console.warn('Offline mode or error fetching scoring log, using cache:', err);
      try {
        const cached = localStorage.getItem(CACHE_HISTORY_PREFIX + userId);
        return cached ? JSON.parse(cached).map(mapHistoryRow) : [];
      } catch {
        return [];
      }
    }
  },

  /**
   * Insulin actually injected on a given date, from the log.
   *
   * The counterpart to plannedTotalDailyDose(): the gap between the two is the
   * adherence signal FR-16 reports, so they are deliberately separate numbers
   * rather than one "TDD".
   */
  async getTodayInsulinTotal(
    userId: string,
    date: string
  ): Promise<{ total: number; injections: number }> {
    const history = await this.getHistory(userId);
    const today = history.filter(r => r.date === date && r.insulinDose);
    return {
      total: today.reduce((sum, r) => sum + (Number(r.insulinDose) || 0), 0),
      injections: today.length
    };
  },

  /**
   * Creates or updates one regimen slot.
   *
   * `productId` and `customProductName` are mutually exclusive — the caller
   * passes whichever the patient chose, and the DB CHECK is the backstop.
   */
  async saveScheduleEntry(
    userId: string,
    entry: {
      id?: string;
      slot: InsulinSlot;
      slotLabel: string;
      timeOfDay?: string | null;
      productId: string | null;
      customProductName: string | null;
      dose: number;
      sortOrder?: number;
    }
  ): Promise<void> {
    assertScheduleEntry(entry);

    const payload = {
      user_id: userId,
      slot: entry.slot,
      slot_label: entry.slotLabel.trim(),
      time_of_day: entry.timeOfDay ?? null,
      product_id: entry.productId,
      custom_product_name: entry.productId ? null : entry.customProductName!.trim(),
      dose: entry.dose,
      sort_order: entry.sortOrder ?? 0,
      updated_at: new Date().toISOString()
    };

    const { error } = entry.id
      ? await supabase.from('insulin_schedules').update(payload).eq('id', entry.id)
      : await supabase.from('insulin_schedules').insert(payload);

    if (error) throw error;
    localStorage.removeItem(CACHE_SCHEDULE_PREFIX + userId);
  },

  /**
   * Removes a slot.
   *
   * A soft delete rather than a hard one: injection_history rows are logged
   * independently and a doctor reviewing an old month should still be able to
   * see what the regimen was, so the row is deactivated, not destroyed.
   */
  async removeScheduleEntry(userId: string, entryId: string): Promise<void> {
    const { error } = await supabase
      .from('insulin_schedules')
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq('id', entryId)
      .eq('user_id', userId);

    if (error) throw error;
    localStorage.removeItem(CACHE_SCHEDULE_PREFIX + userId);
  },

  async addInjection(userId: string, record: HistoryRecord) {
    const payload: InjectionHistoryInsert = {
      user_id: userId,
      date: record.date,
      time: record.time,
      site_id: record.siteId,
      insulin_type_id: record.insulinTypeId,
      age_group: record.ageGroup,
      insulin_brand: record.insulinBrand,
      insulin_product_id: record.insulinProductId ?? null,
      insulin_dose: record.insulinDose,
      glucose_value: record.glucoseValue,
      measurement_context: record.measurementContext ?? null,
      region_id: record.regionId ?? null,
      point_row: record.pointRow ?? null,
      point_col: record.pointCol ?? null,
      lipodystrophy: record.lipodystrophy === true
    };

    try {
      const { error } = await supabase.from('injection_history').insert(payload);
      if (error) throw error;

      // Update local cache for history implicitly?
      // It's better to let getHistory handle cache updates, but we can append to cache to be responsive.
      const cached = localStorage.getItem(CACHE_HISTORY_PREFIX + userId);
      if (cached) {
        const data = JSON.parse(cached);
        data.unshift({ ...payload }); // Prepend new record
        localStorage.setItem(CACHE_HISTORY_PREFIX + userId, JSON.stringify(data));
      }

    } catch (err) {
      console.warn('Offline mode or error saving injection, queuing:', err);
      this.queueOfflineAction(userId, {
        type: 'add_injection',
        payload,
        timestamp: Date.now()
      });

      // Optimistic update for history cache
      const cached = localStorage.getItem(CACHE_HISTORY_PREFIX + userId);
      const data = cached ? JSON.parse(cached) : [];
      data.unshift({ ...payload });
      localStorage.setItem(CACHE_HISTORY_PREFIX + userId, JSON.stringify(data));
    }
  },

  /**
   * Records the point just used, for the rotation suggestion (FR-09).
   *
   * Writes the band-agnostic columns added by migration 006. The pre-FR-08
   * last_site_id_kids / last_site_id_teen columns are left alone so an
   * un-upgraded client keeps working.
   */
  async updateLastPoint(userId: string, band: AgeBand, point: InjectionPoint) {
    const payload = {
      user_id: userId,
      last_region_id: point.regionId,
      last_point_row: point.row,
      last_point_col: point.col,
      last_age_band: band,
      updated_at: new Date().toISOString()
    };

    try {
      const { error } = await supabase.from('last_injection_site').upsert(payload);
      if (error) throw error;
      localStorage.setItem(CACHE_LAST_SITE_PREFIX + userId, JSON.stringify(payload));
    } catch (err) {
      console.warn('Offline mode or error saving last point, queuing:', err);
      this.queueOfflineAction(userId, {
        type: 'update_last_site',
        payload,
        timestamp: Date.now()
      });
      localStorage.setItem(CACHE_LAST_SITE_PREFIX + userId, JSON.stringify(payload));
    }
  },

  async updateLastSite(userId: string, ageGroup: AgeGroup, siteId: number) {
    let currentData: any = null;

    try {
      const { data, error } = await supabase.from('last_injection_site').select('*').eq('user_id', userId).single();
      if (!error) currentData = data;
    } catch {}

    if (!currentData) {
       // Try cache
       const cached = localStorage.getItem(CACHE_LAST_SITE_PREFIX + userId);
       if (cached) currentData = JSON.parse(cached);
    }

    const updateData: any = {
        user_id: userId,
        updated_at: new Date().toISOString(),
        last_site_id_kids: currentData?.last_site_id_kids,
        last_site_id_teen: currentData?.last_site_id_teen
    };

    if (ageGroup === '2-10') {
        updateData.last_site_id_kids = siteId;
    } else {
        updateData.last_site_id_teen = siteId;
    }

    try {
        const { error } = await supabase.from('last_injection_site').upsert(updateData);
        if (error) throw error;
        // Update cache on success
        localStorage.setItem(CACHE_LAST_SITE_PREFIX + userId, JSON.stringify(updateData));
    } catch (err) {
        console.warn('Offline mode or error updating last site, queuing:', err);
        this.queueOfflineAction(userId, {
            type: 'update_last_site',
            payload: updateData,
            timestamp: Date.now()
        });
        // Optimistic cache update
        localStorage.setItem(CACHE_LAST_SITE_PREFIX + userId, JSON.stringify(updateData));
    }
  },

  async getLastSite(userId: string, ageGroup: AgeGroup): Promise<number | null> {
    try {
      const { data, error } = await supabase
        .from('last_injection_site')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) throw error;

      const siteData = data as any;
      localStorage.setItem(CACHE_LAST_SITE_PREFIX + userId, JSON.stringify(siteData));

      if (ageGroup === '2-10') return siteData.last_site_id_kids;
      return siteData.last_site_id_teen;
    } catch (err) {
      console.warn('Offline mode or error fetching last site, using cache:', err);
      const cached = localStorage.getItem(CACHE_LAST_SITE_PREFIX + userId);
      if (cached) {
        try {
          const data = JSON.parse(cached);
          if (ageGroup === '2-10') return data.last_site_id_kids;
          return data.last_site_id_teen;
        } catch {
          return null;
        }
      }
      return null;
    }
  },

  queueOfflineAction(userId: string, action: OfflineAction) {
    const key = OFFLINE_QUEUE_PREFIX + userId;
    const queueRaw = localStorage.getItem(key);
    const queue: OfflineAction[] = queueRaw ? JSON.parse(queueRaw) : [];
    queue.push(action);
    localStorage.setItem(key, JSON.stringify(queue));
  },

  async syncOfflineData(userId: string) {
    const key = OFFLINE_QUEUE_PREFIX + userId;
    const queueRaw = localStorage.getItem(key);
    if (!queueRaw) return;

    const queue: OfflineAction[] = JSON.parse(queueRaw);
    if (queue.length === 0) return;

    console.log(`Syncing ${queue.length} offline actions...`);

    const newQueue: OfflineAction[] = [];

    for (const action of queue) {
      try {
        if (action.type === 'add_injection') {
          const { error } = await supabase.from('injection_history').insert(action.payload as any);
          if (error) throw error;
        } else if (action.type === 'update_last_site') {
           const { error } = await supabase.from('last_injection_site').upsert(action.payload as any);
           if (error) throw error;
        }
      } catch (err) {
        console.error('Failed to sync action:', action, err);
        newQueue.push(action); // Keep in queue if failed
      }
    }

    if (newQueue.length > 0) {
      localStorage.setItem(key, JSON.stringify(newQueue));
    } else {
      localStorage.removeItem(key);
    }
  }
};
